import { useRef, useState } from "react";

import type { ParsedDxfPreview } from "@/lib/dxfParse";
import { findNearestSnap, snapCandidates, type SnapPoint } from "@/lib/dxfSnap";
import {
  SVG_VIEW_H,
  SVG_VIEW_W,
  geometryRectInSvg,
} from "@/lib/dxfTransform";

interface Props {
  parsed: ParsedDxfPreview | null;
  /** Part width in mm (used to convert SVG-space distance back to mm). */
  widthMm: number;
  /** Part height in mm. */
  heightMm: number;
}

/**
 * Click-to-measure overlay for the analyzer's preview.
 *
 * State machine: idle → anchor (1st click) → result (2nd click) → idle
 * (3rd click resets and starts a new measurement at that point).
 *
 * Snap targets come from `lib/dxfSnap.ts`: hole centres + outer-loop
 * corners. SVG-space distances are converted back to mm via the
 * deterministic transform from `lib/dxfTransform.ts`.
 *
 * Caller renders this inside an interactive overlay slot — the SVG
 * itself sets `pointer-events: auto` so clicks land here, not on the
 * pan/zoom surface below.
 */
export function DxfMeasurementOverlay({
  parsed,
  widthMm,
  heightMm,
}: Props): JSX.Element | null {
  const svgRef = useRef<SVGSVGElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const [result, setResult] = useState<{ x: number; y: number } | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number; snap: SnapPoint | null } | null>(null);

  const candidates = snapCandidates(parsed);
  const rect = geometryRectInSvg(widthMm, heightMm);
  const mmPerSvgUnit = rect ? widthMm / rect.w : 0;

  function pointFromEvent(e: React.MouseEvent<SVGSVGElement>): { x: number; y: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const p = svg.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const inv = ctm.inverse();
    const t = p.matrixTransform(inv);
    return { x: t.x, y: t.y };
  }

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const p = pointFromEvent(e);
    if (!p) return;
    const snap = findNearestSnap(p, candidates);
    setHover({ x: p.x, y: p.y, snap: snap?.point ?? null });
  }

  function handleLeave() {
    setHover(null);
  }

  function handleClick(e: React.MouseEvent<SVGSVGElement>) {
    const p = pointFromEvent(e);
    if (!p) return;
    const snap = findNearestSnap(p, candidates);
    const target = snap ? { x: snap.point.x, y: snap.point.y } : p;
    if (anchor === null) {
      setAnchor(target);
      setResult(null);
    } else if (result === null) {
      setResult(target);
    } else {
      // Restart at this point.
      setAnchor(target);
      setResult(null);
    }
  }

  const cursorPoint = hover?.snap ?? hover ?? null;
  const stroke = "#1d4ed8";

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${SVG_VIEW_W} ${SVG_VIEW_H}`}
      className="absolute inset-0 h-full w-full cursor-crosshair"
      // Default SVG pointer-events is ``visiblePainted`` — only painted
      // shapes receive events, empty viewBox area passes through. That
      // killed snap because most cursor positions over the part are
      // empty space. Force ``all`` so onMouseMove fires anywhere
      // inside the viewBox.
      style={{ pointerEvents: "all" }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      data-testid="dxf-measurement-overlay"
    >
      {/* Transparent capture rect so the SVG element actually has a
          painted background to receive pointer events on. Belt-and-
          braces alongside ``pointer-events: all``. */}
      <rect
        x={0}
        y={0}
        width={SVG_VIEW_W}
        height={SVG_VIEW_H}
        fill="transparent"
      />

      {/* Snap target indicator at the cursor */}
      {cursorPoint && (
        <g>
          {hover?.snap ? (
            <circle
              cx={hover.snap.x}
              cy={hover.snap.y}
              r={4}
              fill="#1d4ed8"
              stroke="#ffffff"
              strokeWidth="1"
            />
          ) : (
            hover && (
              <g stroke={stroke} strokeWidth="0.7">
                <line x1={hover.x - 4} y1={hover.y} x2={hover.x + 4} y2={hover.y} />
                <line x1={hover.x} y1={hover.y - 4} x2={hover.x} y2={hover.y + 4} />
              </g>
            )
          )}
        </g>
      )}

      {/* Anchor marker */}
      {anchor && (
        <circle
          cx={anchor.x}
          cy={anchor.y}
          r={3}
          fill={stroke}
          stroke="#ffffff"
          strokeWidth="1"
        />
      )}

      {/* Measurement line — anchor to (result OR live cursor) */}
      {anchor && (result || hover) && (
        <Measurement
          a={anchor}
          b={result ?? { x: hover!.x, y: hover!.y }}
          mmPerSvgUnit={mmPerSvgUnit}
          settled={result !== null}
        />
      )}
    </svg>
  );
}

function Measurement({
  a,
  b,
  mmPerSvgUnit,
  settled,
}: {
  a: { x: number; y: number };
  b: { x: number; y: number };
  mmPerSvgUnit: number;
  settled: boolean;
}): JSX.Element {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const distMm = dist * mmPerSvgUnit;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const stroke = "#1d4ed8";
  const label = `${distMm.toFixed(2)} mm`;
  const labelW = Math.max(48, label.length * 5.5 + 12);
  return (
    <g>
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={stroke}
        strokeWidth="0.9"
        strokeDasharray={settled ? undefined : "3 2"}
      />
      <circle cx={b.x} cy={b.y} r={3} fill={stroke} stroke="#ffffff" strokeWidth="1" />
      <rect
        x={mid.x - labelW / 2}
        y={mid.y - 9}
        width={labelW}
        height={16}
        rx={3}
        fill="#ffffff"
        stroke={stroke}
        strokeWidth="0.7"
      />
      <text
        x={mid.x}
        y={mid.y + 3}
        textAnchor="middle"
        fontSize="10"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fill={stroke}
      >
        {label}
      </text>
    </g>
  );
}
